import { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  TextField,
  Checkbox,
  FormControlLabel,
  Paper,
  Alert,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import SelectOption, {
  ISelectOption,
} from "../../../components/selectOption/SelectOption";
import MainLoader from "../../../components/loading/MainLoader";
import StatusAlertModel from "../../../components/statusAlertModel/StatusAlertModel";
import { getAccountNumbersByToken } from "../../../store/actions/fault.action";
import { ISuccessState } from "../../../interfaces/successState";
import CustomBreadcrumb from "../../../components/customBreadcrumb/CustomBreadcrumb";
import Title from "../../../components/title/Title";
import { BASE_URL } from "../../../const/URL";
import Modal from "react-bootstrap/Modal";
import { FaLock } from "react-icons/fa";
import styles from "./BillMode.module.scss";
import { Spinner } from "react-bootstrap";
import EmptyPlaceholder from "../../../components/emptyPlaceholder/EmptyPlaceholder";
import { set } from "lodash";
import TextInput from "../../../components/textInput/TextInput";

interface UserAccount {
  nickname: string;
  accNo: string;
}

interface BillModeData {
  billMode: string;
  error_Message: string;
  isSuccess: boolean;
}

export enum EInputMode {
  primary,
  light,
}

interface OTPResponse {
  dataBundle: {
    otpReference: string;
  };
  isSuccess: boolean;
  errorMessage: string | null;
  successMessage: string | null;
}

const BillMode = () => {
  const [accounts, setAccounts] = useState<UserAccount[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState<ISuccessState | undefined>();
  const [selectedVal, setSelectedVal] = useState<string>("");
  const [currentBillMode, setCurrentBillMode] = useState<string>("");
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [otpReference, setOtpReference] = useState<string>("");
  const [otp, setOtp] = useState<string[]>(["", "", "", "", "", ""]);
  const [otpError, setOtpError] = useState<string>("");
  const [emailVerified, setEmailVerified] = useState<boolean>(false);
  const [formData, setFormData] = useState<any>(null);

  const billModeOptions: ISelectOption[] = [
    { label: "E-statement", value: "E-statement" },
    {
      label: "Corporate E-Bill",
      value: "Corporate E-Bill",
    },
    {
      label: "Corporate_E-statement by Email",
      value: "Corporate_E-statement by Email",
    },
    {
      label: "Hard Copy",
      value: "Hard Copy",
    },
    {
      label: "Corporate Hard Copy & E-Bill",
      value: "Corporate Hard Copy &amp; E-Bill",
    },
  ];

  const requiresEmailVerification = (billMode: string) => {
    return [
      "E-statement",
      "Corporate Hard Copy &amp; E-Bill",
      "Corporate_E-statement by Email",
    ].includes(billMode);
  };

  const {
    control,
    formState: { errors, isValid },
    setValue,
    handleSubmit,
    watch,
    getValues,
  } = useForm({ mode: "onChange" });

  useEffect(() => {
    accountNumberHandler();
  }, []);

  const accountNumberHandler = async () => {
    setIsLoading(true);
    try {
      const response = await getAccountNumbersByToken();
      if (response) setAccounts(response);
    } catch (error) {
      console.error(error);
      setIsSuccess({
        isSuccess: false,
        message: "Failed to load account numbers",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const accountNumbers: ISelectOption[] = [
    { label: "Select Account Number", value: "" },
    ...(accounts?.map((account: UserAccount) => ({
      label: account.nickname
        ? `${account.accNo} - ${account.nickname}`
        : account.accNo,
      value: account.accNo,
    })) || []),
  ];

  const setAccountNoHandler = (value: string) => {
    setSelectedVal(value);
    if (value) {
      setOtp(["", "", "", "", "", ""]);
      setValue("email", "");
      fetchCurrentBillMode(value);
    } else {
      setCurrentBillMode("");
    }
  };

  const fetchCurrentBillMode = async (sltbssAccountNumber: string) => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem("enterprise_portal_access_Token");
      const response = await fetch(
        `${BASE_URL}/api/v1/ENTCRMBillingProfileUpdate/api/CRMBillingProfileUpdateAPI/ViewCustomerBillMode`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ sltbssAccountNumber }),
        }
      );
      const data: BillModeData = await response.json();

      if (data.isSuccess) {
        setCurrentBillMode(data.billMode);
        setValue("billMode", data.billMode);
      } else {
        setIsSuccess({
          isSuccess: false,
          message: data.error_Message || "Failed to fetch bill mode",
        });
      }
    } catch (error) {
      setIsSuccess({
        isSuccess: false,
        message: "Error fetching current bill mode",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const sendOTP = async (email: string) => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem("enterprise_portal_access_Token");
      const response = await fetch(`${BASE_URL}/api/email/send-otp`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ email }),
      });
      const data: OTPResponse = await response.json();

      if (data.isSuccess) {
        setOtpReference(data.dataBundle.otpReference);
        setShowOtpModal(true);
        return true;
      } else {
        setIsSuccess({
          isSuccess: false,
          message: data.errorMessage || "Failed to send OTP",
        });
        setShowOtpModal(true);
        return false;
      }
    } catch (error) {
      setIsSuccess({
        isSuccess: false,
        message: "Error sending OTP",
      });
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const handleOtpChange = (value: string, index: number) => {
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    // Auto focus to next input
    if (value && index < 5) {
      const nextInput = document.getElementById(`otp-${index + 1}`);
      if (nextInput) nextInput.focus();
    }
  };

  const handleOtpVerify = async () => {
    const otpValue = otp.join("");
    if (otpValue.length !== 6) {
      setOtpError("Please enter a valid 6-digit OTP");
      return;
    }

    setIsLoading(true);
    setOtpError("");
    try {
      const token = localStorage.getItem("enterprise_portal_access_Token");
      const response = await fetch(`${BASE_URL}/api/email/verify-otp`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          otpReference,
          otp: otpValue,
        }),
      });
      const data: OTPResponse = await response.json();

      if (data.isSuccess) {
        setEmailVerified(true);
        setShowOtpModal(false);
        if (requiresEmailVerification(formData.billMode)) {
          await updateBillModeInternal(formData);
        }
      } else {
        setOtpError(data.errorMessage || "OTP verification failed");
      }
    } catch (error) {
      setOtpError("Error verifying OTP");
      setIsLoading(false);
    } finally {
      setIsLoading(true);
    }
  };

  const updateBillMode = async (data: any) => {
    if (!selectedVal) return;

    setFormData(data);

    if (requiresEmailVerification(data.billMode)) {
      const email = getValues("email");
      const success = await sendOTP(email);
      setIsLoading(false);
      if (!success) return;
    } else {
      await updateBillModeInternal(data);
    }
  };

  const updateBillModeInternal = async (data: any) => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem("enterprise_portal_access_Token");
      const requiresEmail = requiresEmailVerification(data.billMode);
      const response = await fetch(
        `${BASE_URL}/api/v1/ENTCRMBillingProfileUpdate/api/CRMBillingProfileUpdateAPI/UpdateCustomerBillMode`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            accountNumber: selectedVal,
            mediaType: data.billMode,
            sltBillingMode2: "E-statement",
            sltBillingMode3: "",
            sltBillMode1Ref: requiresEmail ? data.email : "null",
            sltBillMode2Ref: "null",
            sltBillMode3Ref: "null",
          }),
        }
      );
      const result: BillModeData = await response.json();

      if (result.isSuccess) {
        setIsSuccess({
          isSuccess: true,
          message: "Bill mode updated successfully",
        });
        setCurrentBillMode("");
        setSelectedVal("");
        setOtp(["", "", "", "", "", ""]);
        setValue("email", "");
        setEmailVerified(false);
      } else {
        setIsSuccess({
          isSuccess: false,
          message: result.error_Message || "Failed to update bill mode",
        });
      }
    } catch (error) {
      setIsSuccess({
        isSuccess: false,
        message: "Error updating bill mode",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleBillModeChange = (value: string) => {
    setValue("billMode", value);
  };

  return (
    <div className="page-container">
      <CustomBreadcrumb
        items={[
          { path: "/", title: "Home" },
          { path: "/billing/BillMode", title: "Bill Mode" },
        ]}
      />
      <Title>Bill Mode</Title>
      <MainLoader show={isLoading} />

      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          mt: 4,
        }}
      >
        <Box
          sx={{
            p: 4,
            width: "100%",
            borderRadius: "12px",
          }}
        >
          <form onSubmit={handleSubmit(updateBillMode)}>
            <Controller
              control={control}
              name="accountNumber"
              rules={{ required: true }}
              render={({ field }) => (
                <SelectOption
                  label="Account Number"
                  placeholder="Select Account Number"
                  options={accountNumbers}
                  value={accountNumbers.find(
                    (opt) => opt.value === field.value
                  )}
                  onChange={(val: ISelectOption) => {
                    field.onChange(val.value);
                    setAccountNoHandler(val.value);
                  }}
                />
              )}
            />

            {currentBillMode === null || !selectedVal ? (
              <EmptyPlaceholder className="mt-2" />
            ) : (
              <>
                <TextInput
                  label="Current Bill Method"
                  color="gray"
                  mode={EInputMode.light}
                  disabled
                  value={currentBillMode || "Not selected"}
                  className="mt-2 mb-2"
                />

                <Controller
                  control={control}
                  name="billMode"
                  rules={{ required: true }}
                  render={({ field }) => (
                    <SelectOption
                      label="New Bill Method"
                      placeholder="Select new bill method"
                      options={billModeOptions}
                      value={billModeOptions.find(
                        (opt) => opt.value === field.value
                      )}
                      onChange={(val: ISelectOption) => {
                        field.onChange(val.value);
                        handleBillModeChange(val.value);
                      }}
                      disabled={!selectedVal}
                      className="mb-2"
                    />
                  )}
                />

                {requiresEmailVerification(watch("billMode")) && (
                  <Controller
                    control={control}
                    name="email"
                    rules={{
                      required: "Email is required",
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: "Invalid email address",
                      },
                    }}
                    render={({ field, fieldState: { error } }) => (
                      <TextInput
                        {...field}
                        type="email"
                        label="Email Address"
                        placeholder="Enter your email"
                        color="gray"
                        required
                        mode={EInputMode.light}
                        fontFamily="Poppins"
                        fontSize={16}
                        errors={[
                          {
                            isShow: !!error,
                            message: error?.message || "",
                          },
                        ]}
                        className="mb-3"
                      />
                    )}
                  />
                )}

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    width: "100%",
                    mt: 2,
                  }}
                >
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={!isValid}
                    sx={{
                      backgroundColor: "#51B848",
                      py: 1.5,
                      borderRadius: "8px",
                      fontWeight: "bold",
                      fontSize: "16px",
                      width: "150px",
                      textTransform: "none",
                      "&:hover": {
                        backgroundColor: "#2e9840",
                      },
                      "&:disabled": {
                        backgroundColor: "#51B848",
                        color: "#ffff",
                      },
                    }}
                  >
                    Update
                  </Button>
                </Box>
              </>
            )}
          </form>
        </Box>
      </Box>

      {/* OTP Verification Modal */}
      <Modal
        show={showOtpModal}
        onHide={() => setShowOtpModal(false)}
        centered
        backdrop="static"
        className={styles.otpModal}
      >
        <Modal.Header closeButton className="border-0 pb-0">
          <Modal.Title className="w-100 text-center">
            <div className={styles.otpIconContainer}>
              <FaLock className={styles.otpIcon} />
            </div>
            <h4 className="mt-3">Email Verification</h4>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center pt-0">
          <p className="mb-4">
            We've sent a 6-digit verification code to
            <br />
            <strong>{formData?.email}</strong>
          </p>

          <div className={styles.otpInputs}>
            {otp.map((value, index) => (
              <input
                key={index}
                id={`otp-${index}`}
                type="text"
                inputMode="numeric"
                value={value}
                onChange={(e) => handleOtpChange(e.target.value, index)}
                className={`${styles.otpInput} ${value ? styles.hasValue : ""}`}
                maxLength={1}
                autoComplete="off"
              />
            ))}
          </div>

          {otpError && (
            <Alert variant="standard" severity="error" className="mt-3 mb-0">
              {otpError}
            </Alert>
          )}

          <Button
            variant="contained"
            onClick={handleOtpVerify}
            className={`${styles.verifyOtpBtn} mt-4`}
            disabled={isLoading || otp.join("").length !== 6}
            sx={{
              backgroundColor: "#33A848",
              "&:hover": {
                backgroundColor: "#2e9840",
              },
            }}
          >
            {isLoading ? (
              <Spinner animation="border" size="sm" />
            ) : (
              "Verify OTP"
            )}
          </Button>
        </Modal.Body>
      </Modal>

      <StatusAlertModel isSuccess={isSuccess} setIsSuccess={setIsSuccess} />
    </div>
  );
};

export default BillMode;
